import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Layouts (loaded eagerly — needed for all routes)
import PublicLayout from "@/layouts/PublicLayout";
import AdminLayout from "@/layouts/AdminLayout";

// Loading fallback
const PageLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
    <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#0047AB', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// Public Pages (lazy loaded — each gets its own chunk)
const HomePage = React.lazy(() => import("@/pages/HomePage"));
const AboutPage = React.lazy(() => import("@/pages/AboutPage"));
const ProductsPage = React.lazy(() => import("@/pages/ProductsPage"));
const ProductDetailPage = React.lazy(() => import("@/pages/ProductDetailPage"));
const TradingPage = React.lazy(() => import("@/pages/TradingPage"));
const ManufacturingPage = React.lazy(() => import("@/pages/ManufacturingPage"));
const RFQPage = React.lazy(() => import("@/pages/RFQPage"));
const ContactPage = React.lazy(() => import("@/pages/ContactPage"));
const BlogPage = React.lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = React.lazy(() => import("@/pages/BlogPostPage"));
const DownloadsPage = React.lazy(() => import("@/pages/DownloadsPage"));
const CareersPage = React.lazy(() => import("@/pages/CareersPage"));
const ThankYouPage = React.lazy(() => import("@/pages/ThankYouPage"));
const SEOPagesIndex = React.lazy(() => import("@/pages/SEOPagesIndex"));
const SEOPageWrapper = React.lazy(() => import("@/pages/SEOPageWrapper"));

// Admin Pages (lazy loaded — only loaded when admin navigates there)
const AdminLoginPage = React.lazy(() => import("@/pages/admin/AdminLoginPage"));
const AdminDashboard = React.lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProducts = React.lazy(() => import("@/pages/admin/AdminProducts"));
const AdminRFQs = React.lazy(() => import("@/pages/admin/AdminRFQs"));
const AdminBlog = React.lazy(() => import("@/pages/admin/AdminBlog"));
const AdminSEO = React.lazy(() => import("@/pages/admin/AdminSEO"));
const AdminContent = React.lazy(() => import("@/pages/admin/AdminContent"));
const AdminResources = React.lazy(() => import("@/pages/admin/AdminResources"));
const AdminJobs = React.lazy(() => import("@/pages/admin/AdminJobs"));
const AdminCallbacks = React.lazy(() => import("@/pages/admin/AdminCallbacks"));
const AdminSecurity = React.lazy(() => import("@/pages/admin/AdminSecurity"));
const AdminCMS = React.lazy(() => import("@/pages/admin/AdminCMS"));
const AdminCategories = React.lazy(() => import("@/pages/admin/AdminCategories"));
const AdminCustomers = React.lazy(() => import("@/pages/admin/AdminCustomers"));
const AdminNewsletters = React.lazy(() => import("@/pages/admin/AdminNewsletters"));
const AdminCommunications = React.lazy(() => import("@/pages/admin/AdminCommunications"));
const AdminNewsletterStudio = React.lazy(() => import("@/pages/admin/AdminNewsletterStudio"));

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:productSlug" element={<ProductDetailPage />} />
            <Route path="/trading" element={<TradingPage />} />
            <Route path="/manufacturing" element={<ManufacturingPage />} />
            <Route path="/rfq" element={<RFQPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/downloads" element={<DownloadsPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/seo-pages" element={<SEOPagesIndex />} />
            <Route path="/:slug" element={<SEOPageWrapper />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="rfqs" element={<AdminRFQs />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="seo" element={<AdminSEO />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="resources" element={<AdminResources />} />
            <Route path="jobs" element={<AdminJobs />} />
            <Route path="callbacks" element={<AdminCallbacks />} />
            <Route path="security" element={<AdminSecurity />} />
            <Route path="cms" element={<AdminCMS />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="newsletters" element={<AdminNewsletters />} />
            <Route path="newsletter-studio" element={<AdminNewsletterStudio />} />
            <Route path="communications" element={<AdminCommunications />} />
          </Route>
        </Routes>
      </Suspense>
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

export default App;
